<template>
  <section class="grades-rating">
    <TabsSwitchBar
      v-model="periodTabValue"
      :tabs="periodTabs"
      :role-class="setColorByRole"
      :mobile-equal="true"
    />

    <div class="select__period border-line-bottom" :class="setColorByRole">
      <i class="fas fa-chevron-left period__icon" aria-hidden="true" @click="changePeriod(-1)"></i>
      <div class="period__label period__label--mobile activeLabel">
        <span>{{ getPeriodName(periodIndex) }} {{ t('fourth').toLowerCase() }}</span>
      </div>
      <div
        v-for="item in 4"
        :key="`period-${item}`"
        class="period__label period__label--desktop"
        :class="{ activeLabel: periodIndex === item }"
      >
        <span @click="periodIndex = item">{{ getPeriodName(item) }} {{ t('fourth').toLowerCase() }}</span>
      </div>
      <i class="fas fa-chevron-right period__icon" aria-hidden="true" @click="changePeriod(1)"></i>
    </div>

    <QueryErrorNotice :error="gradesPageErrors" />

    <template v-if="!isLoading">
      <div class="type" v-for="type in byType" :key="`type-${type.type}`">
        <article class="subject pt-3" v-for="(subject, indexSubject) in type.items" :key="`subject-${subject.id}-${indexSubject}`">
          <div class="head">
            <div class="right mr-4" v-if="subject.hasAvgValue" @click="openAvg(subject)" :id="`avg-${type.type}-${indexSubject}`">
              <div class="avg">{{ subject.avgValue }}</div>
            </div>
            <div class="right help__icon mr-3" v-else @click="toggleHelpTooltip($event)" :id="`help-${type.type}-${indexSubject}`">
              <span class="material-icons">help_outline</span>
            </div>
            <div class="center">
              <h3 class="px-3 mb-0 text-truncate subject__name">{{ subject.name }}</h3>
            </div>
          </div>

          <div class="info d-flex flex-wrap">
            <div class="info-border w-100" v-if="subject.rows.length || subject.ekz.length">
              <div class="items__mark horizontal-scroll">
                <div class="d-flex" v-for="(row, rowIndex) in subject.rows" :key="`row-${subject.id}-${rowIndex}`">
                  <div
                    v-for="mark in row"
                    :key="`mark-${mark.id}`"
                    class="mark__default border-white mr-1"
                    :class="`border-${getColorClassByMark(mark)}`"
                  >
                    <div
                      class="mark-value va"
                      :class="`bg-${getColorClassByMark(mark)}-light`"
                      :title="mark.text"
                      @click="toggleMarkTooltip($event, mark)"
                    >
                      <div class="vam mark__style">
                        <span class="mark__text">{{ mark.text }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="items__graph">
                <div class="graph__body">
                  <span class="graph__title">{{ t('gradeGaph') }}</span>
                  <div class="graph" v-if="subject.realMarks.length">
                    <GradesSparklineChart :values="subject.realMarks" :color="subject.gradient" />
                  </div>
                </div>

                <div class="ekz__body">
                  <div
                    v-for="mark in subject.ekz"
                    :key="`ekz-${mark.id}`"
                    class="ekz"
                    :class="[mark.test ? 'test__border' : '', `border-${getColorClassByMark(mark)}`]"
                  >
                    <div :title="mark.text" @click="toggleMarkTooltip($event, mark)">
                      <div class="vam mark__style">
                        <span class="material-icons mr-1" :class="`color-${getColorClassByMark(mark)}`">star</span>
                        <span class="mark__text mark__text--ekz">{{ mark.text }}</span>
                      </div>
                    </div>
                  </div>
                  <span v-if="!subject.ekz.length" class="ekz__empty">
                    {{ t(isPrimarySchool ? 'ekzEmptyPrimary' : 'ekzEmpty') }}
                  </span>
                </div>
              </div>
            </div>

            <div class="text-center no__grade" v-if="!subject.rows.length && !subject.ekz.length">{{ t('noMarks') }}</div>
          </div>
        </article>
      </div>

      <EmptyStateNotice
        v-if="!hasAnySubjects"
        :text="t('noMarks')"
      />
    </template>

    <div v-if="tooltip.show" class="tooltip-overlay" @click="closeTooltip">
      <div class="tooltip-card" :style="{ left: `${tooltip.left}px`, top: `${tooltip.top}px` }" @click.stop>
        <template v-if="tooltip.help">
          {{ t('gradesRatingHelp') }}
        </template>
        <template v-else-if="tooltip.mark">
          <div class="text-center text-muted mb-2" v-if="tooltip.mark.dateText">{{ tooltip.mark.dateText }}</div>
          <div>
            <i class="fa fa-star text-pink" v-if="tooltip.mark.test"></i>
            <i class="fa fa-bookmark text-purple" v-else></i>
            {{ tooltip.mark.theme || '-' }}
          </div>
          <div v-if="tooltip.mark.comment" class="d-flex align-items-center justify-content-start mt-2">
            <span class="material-icons comment mr-2 f-18">mode_comment</span>
            {{ tooltip.mark.comment }}
          </div>
          <div v-if="tooltip.mark.pendingIntermediate" class="text-center text-muted mt-2">
            {{ t('intermediateMarkPending') }}
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import EmptyStateNotice from '@/components/common/EmptyStateNotice.vue';
import GradesSparklineChart from '@/components/common/GradesSparklineChart.vue';
import QueryErrorNotice from '@/components/common/QueryErrorNotice.vue';
import TabsSwitchBar from '@/components/common/TabsSwitchBar.vue';
import {
  useGradesAssignmentsQuery,
  useGradesAverageQuery,
  useGradesMarksQuery,
  usePeriodsQuery
} from '@/queries';
import { useLoadingStore } from '@/stores/loading';
import { useTopbarStore } from '@/stores/topbar';
import { useAvgStore } from '@/stores/avg';
import { useViewerStore } from '@/stores/viewer';
import { parsePeriodsLegacy, toUtcDaySeconds } from '@/utils/periodSlider';

const { t } = useI18n();
const router = useRouter();
const viewerStore = useViewerStore();
const topbarStore = useTopbarStore();
const loadingStore = useLoadingStore();
const avgStore = useAvgStore();
viewerStore.initFromStorage();

const periodIndex = ref(1);
const periodAutoInitialized = ref(false);

const tooltip = ref({
  show: false,
  help: false,
  mark: null,
  left: 0,
  top: 0
});

const byType = ref([
  { type: 1, name: 'ent', items: [] },
  { type: 2, name: 'choice', items: [] },
  { type: 3, name: 'other', items: [] }
]);

const selectedChild = computed(() => viewerStore.selectedChild || null);
const groupId = computed(() => Number(selectedChild.value?.group_id || viewerStore.profile?.group_id || 0));
const pupilId = computed(() => Number(selectedChild.value?.pupil_id || selectedChild.value?._data?.pupil_id || 0));
const studentClassNumber = computed(() => {
  const student = selectedChild.value || viewerStore.profile || {};
  return student?.class_number ?? null;
});
const isPrimarySchool = computed(() => {
  const classNumber = Number(studentClassNumber.value || 0);
  return classNumber >= 1 && classNumber <= 4;
});

const periodsQuery = usePeriodsQuery({}, { staleTime: 5 * 60 * 1000 });
const parsedPeriods = computed(() => parsePeriodsLegacy(periodsQuery.data.value || []));

const periods = computed(() =>
  (parsedPeriods.value?.quarters || []).map((period) => ({
    index: Number(period.number || 0),
    start: Number(period.start || 0),
    end: Number(period.end || 0)
  }))
);
const periodTabs = computed(() =>
  periods.value.map((period) => ({
    value: String(period.index),
    label: `${period.index} ${t('fourth')}`,
    shortLabel: `${period.index} ${t('fourthShort')}`
  }))
);
const periodTabValue = computed({
  get: () => String(periodIndex.value || ''),
  set: (value) => {
    const nextValue = Number(value);
    if (Number.isFinite(nextValue) && nextValue > 0) {
      periodIndex.value = nextValue;
    }
  }
});

const selectedPeriod = computed(
  () => periods.value.find((period) => period.index === Number(periodIndex.value)) || periods.value[0] || null
);

const marksStartTs = computed(() => toUtcDaySeconds(selectedPeriod.value?.start));
const marksEndTs = computed(() => toUtcDaySeconds(selectedPeriod.value?.end));

const assignmentsQuery = useGradesAssignmentsQuery(groupId, {
  placeholderData: (previousData) => previousData,
  retry: false
});
const marksQuery = useGradesMarksQuery(marksStartTs, marksEndTs, pupilId, {
  placeholderData: (previousData) => previousData,
  retry: false
});
const avgQuery = useGradesAverageQuery(periodIndex, pupilId, {
  placeholderData: (previousData) => previousData,
  retry: false
});

const assignments = computed(() => (Array.isArray(assignmentsQuery.data.value) ? assignmentsQuery.data.value : []));
const subjects = computed(() => {
  const subjectMap = new Map();

  assignments.value.forEach((assignment) => {
    const subject = assignment?._subject || assignment?.subject || null;
    const subjectId = Number(subject?.id || assignment?.subject_id || 0);
    if (!subject || subjectId <= 0) return;

    if (!subjectMap.has(subjectId)) {
      subjectMap.set(subjectId, {
        id: subjectId,
        name: subject?.name || '',
        ent_type: Number(subject?.ent_type || 3),
        maLessons: []
      });
    }

    const lessons = assignment?.maLessons || assignment?._maLessons;
    if (lessons && typeof lessons === 'object') {
      Object.entries(lessons).forEach(([lessonId, lesson]) => {
        if (!lesson || typeof lesson !== 'object') return;
        subjectMap.get(subjectId).maLessons.push({
          ...lesson,
          id: lesson?.id || lessonId,
          assignmentId: assignment?.id || null
        });
      });
    }
  });

  return Array.from(subjectMap.values());
});

const marks = computed(() => (Array.isArray(marksQuery.data.value) ? marksQuery.data.value : []));
const avgRows = computed(() => (Array.isArray(avgQuery.data.value) ? avgQuery.data.value : []));

const isLoading = computed(
  () =>
    periodsQuery.isFetching.value ||
    assignmentsQuery.isFetching.value ||
    marksQuery.isFetching.value ||
    avgQuery.isFetching.value
);
const gradesPageErrors = computed(() =>
  [periodsQuery, assignmentsQuery, marksQuery, avgQuery]
    .filter((query) => query.isError?.value)
    .map((query) => query.error?.value)
);

const hasAnySubjects = computed(() => byType.value.some((item) => item.items.length > 0));
const setColorByRole = computed(() => (viewerStore.role === 'child' ? 'active-pupil' : 'active-parent'));

watch(
  () => isLoading.value,
  (value) => {
    loadingStore.set('grades-rating-page', value);
  },
  { immediate: true }
);

watch(
  () => periods.value,
  (rows) => {
    if (!rows.length) return;
    if (!periodAutoInitialized.value) {
      periodIndex.value = findNearestQuarter(rows, Date.now());
      periodAutoInitialized.value = true;
      return;
    }
    if (!rows.some((item) => item.index === Number(periodIndex.value))) {
      periodIndex.value = findNearestQuarter(rows, Date.now());
    }
  },
  { immediate: true }
);

watch(
  () => [subjects.value, marks.value, avgRows.value, selectedPeriod.value],
  () => {
    buildViewModel();
  },
  { immediate: true, deep: true }
);

onMounted(() => {
  topbarStore.setTitle(t('grades_rating'));
  topbarStore.addButton({
    type: 'right',
    icon: 'loop',
    action: () => {
      marksQuery.refetch();
      avgQuery.refetch();
      assignmentsQuery.refetch();
    }
  });
});

onBeforeUnmount(() => {
  loadingStore.hide('grades-rating-page');
  closeTooltip();
});

function buildViewModel() {
  const marksBySubject = groupBySubjectId(marks.value);
  const avgBySubject = new Map(
    avgRows.value.map((item) => [Number(item?._data?.s_id || item?.s_id || 0), item])
  );

  const typeNames = { 1: 'ent', 2: 'choice', 3: 'other' };
  byType.value = [1, 2, 3].map((type) => {
    const typeSubjects = subjects.value.filter((subject) => Number(subject.ent_type || 3) === type);
    const items = typeSubjects.map((subject) => {
      const list = marksBySubject.get(subject.id) || [];
      const preparedMarks = list.map((mark) => normalizeMark(mark));

      const regular = preparedMarks.filter((mark) => {
        if (mark.mId === 105) return true;
        if (mark.test) return false;
        if (mark.percentage === 0) return true;
        return mark.text && mark.lessonView !== 11;
      });

      const arrayLength = Math.ceil(regular.length / 2);
      const rows = arrayLength ? [regular.slice(0, arrayLength), regular.slice(arrayLength)] : [];

      const ekz = preparedMarks.filter((mark) => {
        if (!mark.text) return false;
        if (!mark.test) return false;
        if (mark.mId === 105) return false;
        return mark.percentage !== 0;
      });
      const intermediateMarks = isPrimarySchool.value
        ? normalizeIntermediateLessons(subject.maLessons, subject.id, selectedPeriod.value)
        : ekz;

      const avgItem = avgBySubject.get(subject.id);
      const avgValue = avgItem?._data?.mark_value ?? avgItem?.mark_value ?? null;
      const calculation = avgItem?._data?.calculation ?? avgItem?.calculation ?? null;
      const periodType = avgItem?._data?.period_type ?? avgItem?.period_type ?? null;
      const avgMarkValueNumber = getNumericMarkValue(avgValue);
      const hasRawAvgValue = avgValue !== null && avgValue !== undefined && String(avgValue).trim() !== '';
      const allIntermediateScored =
        intermediateMarks.length > 0 && intermediateMarks.every((mark) => !mark.pendingIntermediate);
      const hasAvgValue = hasRawAvgValue && (!isPrimarySchool.value || allIntermediateScored);

      const realMarks = preparedMarks
        .filter((mark) => !mark.absent && typeof mark.value === 'number' && Number.isFinite(mark.value))
        .map((mark) => mark.value);

      return {
        id: subject.id,
        name: subject.name,
        rows,
        ekz: intermediateMarks,
        avgValue,
        hasAvgValue,
        calculation,
        periodType,
        gradient: avgMarkValueNumber != null ? getColorByAvgValue(avgMarkValueNumber) : '#0199FE',
        realMarks
      };
    });

    return { type, name: typeNames[type], items };
  });
}

function groupBySubjectId(rows) {
  const map = new Map();
  rows.forEach((mark) => {
    const key = Number(mark?.s_id || mark?._data?.s_id || 0);
    if (!key) return;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(mark);
  });
  map.forEach((value) => {
    value.sort((a, b) => new Date(a?.date || 0).getTime() - new Date(b?.date || 0).getTime());
  });
  return map;
}

function normalizeMark(mark) {
  const lplan = mark?._lplan || mark?.lplan || {};
  const lessonView = Number(lplan?.lesson_view || mark?.lesson_view || 0);
  const mId = Number(mark?.m_id || mark?._data?.m_id || 0);
  const markValue = mark?.mark_value ?? mark?._data?.mark_value ?? '';
  const comment = mark?.comment || mark?._data?.comment || '';
  const max = mId === 105 ? 10 : Number(mark?.so_ball || lplan?.so_ball || mark?.max || 0);
  const rawBall = Number(mark?.ball ?? mark?._data?.ball ?? mark?.percent ?? mark?._data?.percent ?? 0);
  const percentage = max > 0 ? Math.floor((rawBall / max) * 10000) / 100 : 0;
  const isCriterial = lessonView === 21 || lessonView === 22 || lessonView === 33;
  const markValueText = String(markValue || '').trim();
  const isZeroAbsent = isAbsentLikeMarkValue(markValueText, isCriterial, rawBall);

  let text = '';
  let value = null;
  if (isZeroAbsent) {
    text = 'Н';
    value = null;
  } else if (mId === 105) {
    text = `${rawBall}/10`;
    value = rawBall;
  } else if (isCriterial && max > 0) {
    text = `${rawBall}/${max}`;
    value = Math.round((rawBall / max) * 10);
  } else if (mId === 104 && comment) {
    text = comment;
    value = null;
  } else if (markValueText && /^(\d.*|Н|H|Ж)$/i.test(markValueText)) {
    text = markValueText;
    value = parseMarkValueNumber(markValueText);
  } else {
    text = /^(\d.*|Н|H|Ж)$/i.test(String(markValue)) ? String(markValue) : '';
    const parsed = Number(markValue);
    value = Number.isFinite(parsed) ? parsed : null;
  }

  const dateValue = mark?.date || mark?._data?.date || '';
  const date = dateValue ? new Date(dateValue) : null;
  const dateText = date && !Number.isNaN(date.getTime()) ? formatDate(date) : '';
  const theme = lplan?.theme_name || '';
  return {
    ...mark,
    id: mark?.id || mark?._id || Math.random().toString(36).slice(2),
    mId,
    lessonView,
    max,
    ball: rawBall,
    percentage: Math.max(0, Math.min(100, percentage || 0)),
    text,
    value,
    absent: ['Н', 'H', 'Ж'].includes(String(markValue)) || isZeroAbsent,
    cTypes: {
      sop: lessonView === 21,
      soq: lessonView === 22,
      intermediate: lessonView === 33
    },
    test: lessonView === 21 || lessonView === 22 || lessonView === 33,
    theme,
    comment,
    dateText
  };
}

function normalizeIntermediateLessons(rawLessons, subjectId, period) {
  const start = Number(period?.start || 0);
  const end = Number(period?.end || 0);

  return (Array.isArray(rawLessons) ? rawLessons : [])
    .map((lesson, index) => {
      const dateValue = lesson?.date || '';
      const date = parseDateOnly(dateValue);
      const dateTs = date?.getTime() || 0;
      const rawMark = lesson?.mark;
      const hasMark = rawMark !== null && rawMark !== undefined && String(rawMark).trim() !== '';
      const ball = hasMark ? Number(rawMark) : 0;
      const max = Number(lesson?.so_ball || 0);
      const safeBall = Number.isFinite(ball) ? ball : 0;
      const text = hasMark
        ? (max > 0 ? `${rawMark}/${max}` : String(rawMark))
        : t('avgLegendIntermediate');

      return {
        ...lesson,
        id: `ma-${subjectId}-${lesson?.assignmentId || 0}-${lesson?.id || index}`,
        mId: 0,
        lessonView: 33,
        max,
        ball: safeBall,
        percentage: hasMark && max > 0 ? Math.max(0, Math.min(100, (safeBall / max) * 100)) : 0,
        text,
        value: hasMark && Number.isFinite(ball) ? ball : null,
        absent: false,
        test: true,
        theme: lesson?.theme_name || '',
        comment: '',
        dateText: date ? formatDate(date) : '',
        dateTs,
        pendingIntermediate: !hasMark
      };
    })
    .filter((lesson) => !lesson.dateTs || !start || !end || (lesson.dateTs >= start && lesson.dateTs <= end))
    .sort((a, b) => a.dateTs - b.dateTs);
}

function parseDateOnly(value) {
  const match = String(value || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(date) {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
}

function getNumericMarkValue(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  const normalized = String(value).replace(',', '.').trim();
  const direct = Number(normalized);
  if (Number.isFinite(direct)) return direct;

  const match = normalized.match(/-?\d+(\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0]);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseMarkValueNumber(value) {
  const source = String(value || '').trim();
  if (!source) return null;
  const match = source.match(/-?\d+([.,]\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0].replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

function isAbsentLikeMarkValue(markValueText, isCriterial, rawBall) {
  if (['Н', 'H', 'Ж'].includes(markValueText)) return true;
  return false;
}

function findNearestQuarter(rows, nowTs) {
  const rowsSafe = Array.isArray(rows) ? rows : [];
  if (!rowsSafe.length) return 1;

  const now = Number(nowTs || Date.now());
  const current = rowsSafe.find((row) => now >= Number(row.start || 0) && now <= Number(row.end || 0));
  if (current) return Number(current.index || 1);

  let nearest = rowsSafe[0];
  let minDistance = Number.POSITIVE_INFINITY;
  rowsSafe.forEach((row) => {
    const start = Number(row.start || 0);
    const end = Number(row.end || 0);
    const distance = now < start ? start - now : now - end;
    if (distance < minDistance) {
      minDistance = distance;
      nearest = row;
    }
  });
  return Number(nearest.index || 1);
}

function getColorClassByMark(mark) {
  if (mark.pendingIntermediate) return 'grey';
  if (mark.mId === 104) return 'empty';
  if (mark.absent) return 'grey';

  let colorMark = mark.value;
  if (mark.lessonView === 21 || mark.lessonView === 22 || mark.lessonView === 33) {
    colorMark = Math.round((Number(mark.ball || 0) / Math.max(1, Number(mark.max || 0))) * 10);
  }

  if (colorMark >= 9) return 'green';
  if (colorMark >= 7) return 'yellow';
  if (colorMark >= 4) return 'orange';
  if (colorMark >= 0) return 'red';
  return 'empty';
}

function getColorByAvgValue(mark) {
  if (mark >= 5) return '#0D8740';
  if (mark >= 4) return '#9BCA16';
  if (mark >= 2) return '#F2C94C';
  return '#EB5757';
}

function getPeriodName(period) {
  const names = t('fourthName');
  if (Array.isArray(names) && names[period - 1]) return names[period - 1];
  return `${period}`;
}

function changePeriod(offset) {
  const pageNum = Number(periodIndex.value) + Number(offset || 0);
  if (pageNum > 4) {
    periodIndex.value = 1;
    return;
  }
  if (pageNum < 1) {
    periodIndex.value = 4;
    return;
  }
  periodIndex.value = pageNum;
}

function openAvg(subject) {
  if (!subject?.hasAvgValue) return;
  avgStore.setData({
    quarter: periodIndex.value,
    subject: subject.name,
    type: subject.periodType,
    classNumber: studentClassNumber.value,
    grade: subject.avgValue,
    formula: subject.calculation
  });
  router.push('/avg');
}

function toggleHelpTooltip(event) {
  const rect = event.currentTarget.getBoundingClientRect();
  tooltip.value = {
    show: true,
    help: true,
    mark: null,
    left: Math.max(12, rect.left - 120),
    top: rect.bottom + 10
  };
}

function toggleMarkTooltip(event, mark) {
  const rect = event.currentTarget.getBoundingClientRect();
  tooltip.value = {
    show: true,
    help: false,
    mark,
    left: Math.max(12, rect.left - 60),
    top: rect.bottom + 10
  };
}

function closeTooltip() {
  tooltip.value.show = false;
  tooltip.value.help = false;
  tooltip.value.mark = null;
}
</script>

<style scoped lang="scss">
.grades-rating {
  padding: 16px;
}

.select__period {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  font-weight: 600;
  font-size: 20px;
  color: #8b8b8d;
  height: 78px;
  margin-top: 40px;
}

@media screen and (max-width: 575px) {
  .select__period {
    padding: 0 12px;
  }
}

.period__label {
  color: #bdbdbd;
}

@media screen and (max-width: 767px) {
  .period__label--desktop {
    display: none;
  }

  .period__label--mobile {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    flex: 1 1 auto;
    border-left: 2px solid transparent;
    padding-left: 10px;
  }
  //.select__period.active-parent .period__label--mobile.activeLabel {
  //  border-left-color: #00c2ff;
  //}
  .select__period{
    margin-top: 0px;
  }

}

@media screen and (min-width: 768px) {
  .period__label--mobile {
    display: none;
  }

  .period__label--desktop {
    display: flex;
  }
}

.activeLabel {
  color: #8b8b8d;
}

.period__icon {
  font-size: 20px !important;
  cursor: pointer;
}

.type {
  padding-bottom: 0;
}

.type__title {
  padding: 8px 16px 0;
  font-size: 14px;
  font-weight: 700;
  color: #8b8b8d;
  text-transform: uppercase;
}

.subject {
  box-shadow: none !important;
  padding-bottom: 13px;
  border: none !important;
  border-bottom: 1px dotted #bdbdbd !important;
}

.head {
  overflow: hidden;
}

.head .center {
  width: auto;
  overflow: hidden;
}

.head .right {
  float: right;
}

.subject__name {
  font-style: normal;
  font-weight: 600;
  font-size: 17px;
}

.avg {
  font-weight: bold;
  border-radius: 50%;
  border: 2px solid #0199fe;
  color: #0199fe;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  cursor: pointer;
}

.help__icon {
  color: #0199fe;
  cursor: pointer;
}

.info {
  margin-top: 16px;
}

.info-border {
  border: 1px solid #e0e0e0;
  border-radius: 5px;
}

.items__mark {
  padding: 8px;
  height: 81px;
  border-bottom: 1px solid #e0e0e0;
  overflow-x: auto;
  overflow-y: hidden;
}

.items__graph {
  display: flex;
  min-height: 83px;
  white-space: nowrap;
  text-align: center;
  justify-content: center;
  width: 100%;
}

.graph__body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  box-shadow: none !important;
  position: relative;
}

.graph__title {
  font-size: 12px;
  color: #000;
  position: absolute;
  left: 28px;
  margin-top: 9px;
}

.graph {
  margin-top: 20px;
  height: 73px;
  padding: 0 12px 2px;
}

.ekz__body {
  display: flex;
  flex-direction: column;
  padding: 8px 8px 0;
  border-left: 1px solid #e0e0e0;
  min-height: 83px;
  flex: 0 0 105px;
  justify-content: center;
  align-items: center;
}

.ekz {
  margin-bottom: 8px;
  color: #000;
  font-weight: 500;
}

.ekz .material-icons {
  font-size: 13px !important;
}

.mark__style {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 10px;
  min-width: 0;
  max-width: 100%;
  white-space: nowrap;
}

.mark__text {
  display: inline-block;
  margin-top: 2px;
  min-width: 0;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: top;
  white-space: nowrap;
}

.mark__text--ekz {
  max-width: 58px;
}

.ekz__empty {
  font-size: 11px;
  white-space: normal;
  color: #8b8b8d;
}

.mark-value {
  min-width: 32px;
  max-width: 220px;
  padding: 0 8px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  font-weight: bold;
  border-radius: 4px;
  text-align: center;
  border: 2px solid #fff;
  background-color: rgba(0, 0, 0, 0.03);
  cursor: pointer;
}

.no__grade {
  border: 1px solid #fbad2f;
  color: #fbad2f !important;
  border-radius: 4px;
  width: 100%;
}

.test__border {
  border: 2px solid;
  border-radius: 5px !important;
}

.border-yellow {
  border-color: #9bca16 !important;
}

.border-red {
  border-color: #eb5757 !important;
}

.border-orange {
  border-color: #f2c94c !important;
}

.border-green {
  border-color: #0d8740 !important;
}

.border-grey {
  border-color: #bdbdbd !important;
}

.border-empty {
  border-color: #bdbdbd !important;
}

.color-yellow {
  color: #9bca16;
}

.color-orange {
  color: #f2c94c;
}

.color-red {
  color: #eb5757;
}

.color-green {
  color: #0d8740;
}

.color-grey {
  color: #bdbdbd;
}

.color-empty {
  color: #8b8b8d;
}

.bg-green-light {
  background-color: #0d8740;
  color: #fff;
}

.bg-yellow-light {
  background-color: #9bca16;
  color: #fff;
}

.bg-orange-light {
  background-color: #f2c94c;
  color: #fff;
}

.bg-red-light {
  background-color: #e74c3c;
  color: #fff;
}

.bg-grey-light {
  background-color: #bdbdbd;
  color: #fff;
}

.bg-empty-light {
  color: #000;
}

.tooltip-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
}

.tooltip-card {
  position: fixed;
  max-width: 320px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px 12px;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.15);
  color: #4f4f4f;
  font-size: 13px;
}

.comment {
  color: #0199fe;
  margin-left: -2px;
}

.horizontal-scroll::-webkit-scrollbar {
  width: 3px;
  height: 3px;
}

.horizontal-scroll::-webkit-scrollbar-thumb {
  background: #d2d2d4;
}

.horizontal-scroll::-webkit-scrollbar-track {
  background: #fff;
  box-shadow: inset 7px 10px 12px #f0f0f0;
}

@media screen and (max-width: 767px) {
  .grades-rating {
    padding: 0px 0 4px;
  }

  .select__period {
    font-size: 16px;
    height: 64px;
    padding: 0 12px;
  }

  .subject {
    padding-bottom: 10px;
    padding-inline: 12px;
  }

  .subject__name {
    font-size: 16px;
  }

  .info-border {
    border-radius: 4px;
  }

  .items__mark {
    height: 76px;
    padding: 6px 8px;
  }

  .items__graph {
    min-height: 72px;
  }

  .graph__title {
    left: 16px;
    margin-top: 8px;
  }

  .graph {
    margin-top: 18px;
    height: 54px;
    padding: 0 8px 2px;
  }

  .ekz__body {
    flex-basis: 92px;
    padding: 6px 6px 0;
  }

  .mark-value {
    min-width: 30px;
    height: 30px;
    padding: 0 6px;
  }
}

@media screen and (min-width: 768px) {
  .items__mark {
    height: 81px;
  }

  .graph {
    height: 73px;
  }

  .info-border {
    border-radius: 5px;
  }
}
</style>
